/* eslint-disable max-lines */
import { IconNames } from '@blueprintjs/icons';
import {
    ActionButtonPageTitle,
    ActionButtonText,
    Button,
    ToggleButton,
} from '@strata/react-buttons';
import { Hyperlink } from '@strata/react-hyperlink';
import { Icon } from '@strata/react-status-icons';

import { Columns } from 'components/shared/Columns';
import { ContentBox } from 'components/shared/ContentBox';
import { Page } from 'components/shared/Page';
import { Section } from 'components/shared/Section';

const buttonOverview = [
    <p key="1">
        Button hierarchy helps users understand the importance of various actions, promotes
        efficient interaction by directing attention to the most relevant tasks, and enables
        intuitive navigation through our applications. By structuring buttons thoughtfully, users
        can complete their goals more quickly and with less cognitive effort.
    </p>,
    <div key="2">
        <div className="sds_example__column-container">
            <span className="sds_example__column-header" style={{ width: '31%' }}>
                Component
            </span>
            <span className="sds_example__column-header" style={{ width: '69%' }}>
                Description
            </span>
        </div>
        <ContentBox
            content={
                <>
                    <div style={{ width: '30%' }}>
                        <Button intent={Button.Intent.PRIMARY} label="Primary" />
                    </div>
                    <div style={{ width: '70%' }}>
                        The primary button is usually the most visually prominent which highlights
                        the main action on a page or within a section.
                    </div>
                </>
            }
        />
        <ContentBox
            content={
                <>
                    <div style={{ width: '30%' }}>
                        <Button intent={Button.Intent.NONE} label="Secondary" />
                    </div>
                    <div style={{ width: '70%' }}>
                        Secondary buttons are less visually distinct than primary buttons. They
                        represent alternative actions or options that are still important but not as
                        critical as the primary action.
                    </div>
                </>
            }
        />
        <ContentBox
            content={
                <>
                    <div style={{ width: '30%' }}>
                        <ActionButtonText label="Action Button" />
                    </div>
                    <div style={{ width: '70%' }}>
                        Action buttons are used less often then primary and secondary buttons. They
                        usually represent less critical independent or supporting actions.
                    </div>
                </>
            }
        />
        <ContentBox
            content={
                <>
                    <div style={{ width: '30%' }}>
                        <ActionButtonPageTitle label="Button" title="Title" />
                    </div>
                    <div style={{ width: '70%' }}>
                        Navigation buttons are placed in upper left of pages and lets users switch
                        views.
                    </div>
                </>
            }
        />
        <ContentBox
            content={
                <>
                    <div style={{ width: '30%' }}>
                        <div
                            style={{
                                display: 'flex',
                                gap: '16px',
                            }}
                        >
                            <Icon
                                icon={IconNames.EDIT}
                                intent={Icon.Intent.DEFAULT}
                                tooltipContent="Edit"
                            />
                            <Icon
                                icon={IconNames.TRASH}
                                intent={Icon.Intent.DEFAULT}
                                tooltipContent="Delete"
                            />
                        </div>
                    </div>
                    <div style={{ width: '70%' }}>
                        Icons are used to represent easily recognizable and universally understood
                        supporting actions.
                    </div>
                </>
            }
        />
        <ContentBox
            content={
                <>
                    <div style={{ width: '30%' }}>
                        <ToggleButton
                            label="Optional Label"
                            helperText="Optional helper text"
                            isOn
                        />
                    </div>
                    <div style={{ width: '70%' }}>
                        Toggle buttons are used to represent a binary choice, where actions are
                        immediate. They should not be used when a save action is needed to apply the
                        update.
                    </div>
                </>
            }
        />
        <ContentBox
            content={
                <>
                    <div style={{ width: '30%' }}>
                        <Hyperlink
                            linkText="Hyperlink"
                            navUrl="https://www.freemarkethealth.com"
                            openInNewTab
                        />
                    </div>
                    <div style={{ width: '70%' }}>
                        Hyperlinks are commonly used to redirect users to another page, or external
                        resource.
                    </div>
                </>
            }
        />
    </div>,
];

const primaryButton = [
    <p key="3">
        Primary buttons are used for the most important action on a screen or within a form. This
        action is usually the intended outcome of the user&#39;s interaction.
    </p>,
    <div key="4">
        <h3>Placement and Use</h3>
        <Columns
            columns={Columns.ColumnNumber.TWO}
            content={
                <>
                    <div>
                        <h4>Overview and detail pages</h4>
                        <p>
                            Primary buttons always appear in the top-right corner of both overview
                            and detail pages.
                        </p>
                    </div>
                    <ContentBox
                        centerContent
                        content={
                            <img
                                style={{ width: 197 }}
                                src="https://sseasacdn.blob.core.windows.net/strata-website/Primary-Button-Placement-Overview-and-Detail-Pages.svg"
                                alt="Button placement in top right corner of detail pages"
                                loading="lazy"
                            />
                        }
                    />
                    <div>
                        <h4>Dialogs</h4>
                        <p>
                            In a dialog, primary buttons are always placed in the bottom right
                            corner. The primary button within a button group should be the rightmost
                            button, and the group itself should be right-aligned.
                        </p>
                    </div>
                    <ContentBox
                        centerContent
                        content={
                            <img
                                style={{ width: 197 }}
                                src="https://sseasacdn.blob.core.windows.net/strata-website/Primary-and-Secondary-Button-Placement-Dialogs.svg"
                                alt="Primary button placement bottom right of dialog"
                                loading="lazy"
                            />
                        }
                    />
                    <div>
                        <h4>Form</h4>
                        <p>
                            On a form page, the button group should be placed at the bottom left of
                            the page, with the primary button as the rightmost of the group.
                        </p>
                    </div>
                    <ContentBox
                        centerContent
                        content={
                            <img
                                style={{ width: 197 }}
                                src="https://sseasacdn.blob.core.windows.net/strata-website/Primary-Button-Placement-Forms.svg"
                                alt="Primary button placement bottom left of a form"
                                loading="lazy"
                            />
                        }
                    />
                </>
            }
        />
    </div>,
    <div key="5">
        <h4>Exception</h4>
        <p>
            As a general rule, there should not be more than one primary action per page. If a page
            contains multiple actions that are equally important, the buttons must be placed within
            different sections of the page and positioned close to their related content.
        </p>
    </div>,
    <div key="6">
        <ContentBox
            centerContent
            content={
                <img
                    style={{ width: 532 }}
                    src="https://sseasacdn.blob.core.windows.net/strata-website/Primary-Button-Exception.svg"
                    alt="Primary button placement by a component on the page"
                    loading="lazy"
                />
            }
        />
        <div className="sds_text__caption">
            In this example, one primary button allows the user to add states to their state list
            (the primary purpose of using this configuration page), and a second primary button
            saves their changes.
        </div>
    </div>,
    <div key="7">
        <h3>Disabled State</h3>
        <p>When a button is in a disabled state it cannot be interacted with.</p>
    </div>,
    <ContentBox
        key="8"
        centerContent
        content={
            <div style={{ display: 'flex', gap: '16px' }}>
                <img
                    style={{ width: 258 }}
                    src="https://sseasacdn.blob.core.windows.net/strata-website/Primary-Button-Dialog-Disabled-State.svg"
                    alt="Dialog showing disabled primary button"
                    loading="lazy"
                />
                <img
                    style={{ width: 258 }}
                    src="https://sseasacdn.blob.core.windows.net/strata-website/Primary-Button-Dialog-Active-State.svg"
                    alt="Dialog showing enabled primary button"
                    loading="lazy"
                />
            </div>
        }
    />,
    <Columns
        key="9"
        columns={Columns.ColumnNumber.TWO}
        content={
            <>
                <div>
                    <h3>Destructive Variant</h3>
                    <p>
                        A variant of the primary button used to visually warn the user that the
                        action they&#39;re about to take is destructive. The destructive variant is
                        only used in confirmation dialogs for critical actions.
                    </p>
                </div>
                <ContentBox
                    centerContent
                    content={
                        <img
                            style={{ width: 197 }}
                            src="https://sseasacdn.blob.core.windows.net/strata-website/Primary-Button-Destructive-Variant.svg"
                            alt="Dialog showing a destructive button to the right of secondary button"
                            loading="lazy"
                        />
                    }
                />
                <div>
                    <h3>Dropdown Variant</h3>
                    <p>Dropdowns are used to group related actions under a single button.</p>
                    <h4>When to use a dropdown button</h4>
                    <ul>
                        <li>When actions depend on the context or selection the user has made.</li>
                        <li>
                            When there are three or more secondary or tertiary actions that would
                            otherwise clutter the interface if displayed individually.
                        </li>
                    </ul>
                </div>
                <ContentBox
                    centerContent
                    content={
                        <img
                            style={{ width: 197 }}
                            src="https://sseasacdn.blob.core.windows.net/strata-website/Primary-Button-Dropdown-Variant.svg"
                            alt="Primary button with dropdown menu"
                            loading="lazy"
                        />
                    }
                />
            </>
        }
    />,
];

const secondaryButton = [
    <p key="10">
        Secondary buttons represent additional actions that are less important than the primary
        actions.
    </p>,
    <div key="11">
        <h3>Placement and Use</h3>
        <Columns
            columns={Columns.ColumnNumber.TWO}
            content={
                <>
                    <div>
                        <h4>Dialogs</h4>
                        <p>
                            In a dialog with multiple buttons, the secondary button should be placed
                            to the left of the primary button. When used in combination with a
                            primary button, the secondary button should be used as an alternate
                            option or dismiss action.
                        </p>
                    </div>
                    <ContentBox
                        centerContent
                        content={
                            <img
                                style={{ width: 250 }}
                                src="https://sseasacdn.blob.core.windows.net/strata-website/Secondary-Button-When-to-Use.svg"
                                alt="Secondary button placed to the left of primary button"
                                loading="lazy"
                            />
                        }
                    />
                    <div>
                        <h4>Acknowledgement Dialogs</h4>
                        <p>
                            In a dialog when there is no primary action to be taken a secondary
                            button should be used alone as an acknowledgment button. If the
                            secondary button is used as an acknowledgment button, it should be
                            placed at the bottom right of the dialog.
                        </p>
                    </div>
                    <ContentBox
                        centerContent
                        content={
                            <img
                                style={{ width: 250 }}
                                src="https://sseasacdn.blob.core.windows.net/strata-website/Secondary-Button-Acknowledgment.svg"
                                alt="Secondary button as an acknowledgment button"
                                loading="lazy"
                            />
                        }
                    />
                    <div>
                        <h4>Forms</h4>
                        <p>
                            On a form page, the secondary button should be placed at the bottom left
                            of the footer, to the left of the primary button.
                        </p>
                    </div>
                    <ContentBox
                        centerContent
                        content={
                            <img
                                style={{ width: 197 }}
                                src="https://sseasacdn.blob.core.windows.net/strata-website/Primary-Button-Placement-Forms.svg"
                                alt="Form with the secondary button place to the left of primary button in the footer"
                                loading="lazy"
                            />
                        }
                    />
                </>
            }
        />
    </div>,
];

const actionButton = [
    <div key="12">
        <p>
            Action buttons consist of text and, optionally, an icon placed to the left of the text.
            Unlike primary buttons, they are passively available, allowing users to engage with them
            when convenient, rather than emphasizing them as the primary action of the workflow.
            They are ideal for utility actions such as:
        </p>
        <ul>
            <li>Clear</li>
            <li>Create</li>
            <li>Delete</li>
            <li>Duplicate</li>
            <li>Edit</li>
        </ul>
    </div>,
    <div key="13">
        <h3>Action Button with an Icon</h3>
        <Columns
            columns={Columns.ColumnNumber.TWO}
            content={
                <>
                    <div>
                        <h4>When to use an action button with an icon</h4>
                        <p>
                            If real estate is not a concern, an action button can be used with an
                            icon for clearer communication.
                        </p>
                    </div>
                    <ContentBox
                        centerContent
                        content={
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '16px',
                                }}
                            >
                                <ActionButtonText
                                    label="Duplicate Rule"
                                    leftIcon={IconNames.DUPLICATE}
                                />
                                <ActionButtonText label="Delete Rule" leftIcon={IconNames.TRASH} />
                            </div>
                        }
                    />
                </>
            }
        />
    </div>,
    <div key="14">
        <h3>Dropdown Variant</h3>
        <p>
            In the scenario where there is a repetitive pattern, such as list where each item offers
            multiple actions, using a dropdown action button for each item is ideal.
        </p>
    </div>,
    <div key="15">
        <ContentBox
            centerContent
            content={
                <img
                    style={{ width: 532 }}
                    src="https://sseasacdn.blob.core.windows.net/strata-website/Action-Button-Dropdown-Variant.svg"
                    alt="Action button dropdowns in a list of cards"
                    loading="lazy"
                />
            }
        />
        <div className="sds_text__caption">
            In this example, the Worklist page features task cards, each containing one or more
            actions. This makes it an ideal scenario for using the action dropdown variant.
        </div>
    </div>,
    <div key="16">
        <h3>Placement and Arrangement</h3>
        <p>
            Buttons should be placed within or close to their related content to establish a clear
            connection. Ensure that the action button follows the natural reading or interaction
            flow (left to right or top to bottom). Position it after the user has reviewed the
            relevant content, making it the next logical step in the process.
        </p>
    </div>,
    <Columns
        key="17"
        columns={Columns.ColumnNumber.TWO}
        content={
            <>
                <div>
                    <h4>Left to right</h4>
                    <p>
                        When the natural/interaction flow is left to right, the action button should
                        be right aligned.
                    </p>
                </div>
                <ContentBox
                    centerContent
                    content={
                        <img
                            style={{ width: 252 }}
                            src="https://sseasacdn.blob.core.windows.net/strata-website/Action-Button-Placement-Left-to-Right.svg"
                            alt="Action button to the right of content"
                            loading="lazy"
                        />
                    }
                />
                <div>
                    <h4>Top to bottom</h4>
                    <p>
                        When the natural/interaction flow is top to bottom, the action button should
                        be placed below the content.
                    </p>
                </div>
                <ContentBox
                    centerContent
                    content={
                        <img
                            style={{ width: 252 }}
                            src="https://sseasacdn.blob.core.windows.net/strata-website/Action-Button-Placement-Top-to-Bottom.svg"
                            alt="Action button at the bottom of the content"
                            loading="lazy"
                        />
                    }
                />
            </>
        }
    />,
];

const navigationButton = [
    <div key="18">
        <p>
            The actual navigation button itself is the previous page&#39;s name, followed by a
            vertical bar, and the current page&#39;s name.
        </p>
    </div>,
    <ContentBox
        key="19"
        centerContent
        content={<ActionButtonPageTitle label="Referrals" title="Referral Details" />}
    />,
    <div key="20">
        <h3>Placement</h3>
        <p>
            A navigation button&#39;s primary function is to return the user to the originating
            page. Therefore, navigation buttons should exclusively appear on nested pages, not on
            main pages. Navigation buttons should be made available in the top left corner of all
            nested details pages.
        </p>
    </div>,
    <ContentBox
        key="21"
        centerContent
        content={
            <img
                style={{ width: 532 }}
                src="https://sseasacdn.blob.core.windows.net/strata-website/Navigation-Button-Placement.svg"
                alt="Navigation button on top left corner of nested details page"
                loading="lazy"
            />
        }
    />,
];

const iconOnlyButton = [
    <Columns
        key="22"
        columns={Columns.ColumnNumber.TWO}
        content={
            <>
                <p>
                    Icon only buttons are ideal when space is limited and the action is easily
                    recognizable. They remain passively available, allowing users to interact with
                    them when ready. These buttons are mainly used for utility actions.
                </p>
                <ContentBox
                    centerContent
                    content={
                        <div style={{ display: 'flex', gap: '16px' }}>
                            <Icon
                                icon={IconNames.CROSS}
                                intent={Icon.Intent.DEFAULT}
                                tooltipContent="Close"
                            />
                            <Icon
                                icon={IconNames.EDIT}
                                intent={Icon.Intent.DEFAULT}
                                tooltipContent="Edit"
                            />
                            <Icon
                                icon={IconNames.ADD}
                                intent={Icon.Intent.DEFAULT}
                                tooltipContent="Create"
                            />
                            <Icon
                                icon={IconNames.DUPLICATE}
                                intent={Icon.Intent.DEFAULT}
                                tooltipContent="Duplicate"
                            />
                            <Icon
                                icon={IconNames.TRASH}
                                intent={Icon.Intent.DEFAULT}
                                tooltipContent="Delete"
                            />
                            <Icon
                                icon={IconNames.SEARCH}
                                intent={Icon.Intent.DEFAULT}
                                tooltipContent="Search"
                            />
                        </div>
                    }
                />
                <div>
                    <h3>Collapse/Expand Buttons</h3>
                    <p>
                        Chevron icons are used to quickly hide and unhide sections. They should
                        always be placed to the right of their related content.
                    </p>
                </div>
                <ContentBox
                    centerContent
                    content={
                        <img
                            style={{ width: 189 }}
                            src="https://sseasacdn.blob.core.windows.net/strata-website/Collapse-Expand-Buttons.svg"
                            alt="Collapse and expand buttons in filter pane"
                            loading="lazy"
                        />
                    }
                />
                <div>
                    <h3>Meatball Icon Button</h3>
                    <p>
                        The meatball icon button can be used in grids, tiles, or cards as a
                        space-efficient way to provide users with additional actions.
                    </p>
                    <h4>When to use a meatball icon button column</h4>
                    <p>
                        If there are one or more related actions, that are not utility actions,
                        group them under a meatball menu. Here are some examples:
                    </p>
                    <ul>
                        <li>Set to first fill in progress</li>
                        <li>Go to Patient Treatment</li>
                        <li>Fix invalid data</li>
                    </ul>
                </div>
                <ContentBox
                    centerContent
                    content={
                        <img
                            style={{ width: 197 }}
                            src="https://sseasacdn.blob.core.windows.net/strata-website/Meatball-Icon.svg"
                            alt="Meatball icon button"
                            loading="lazy"
                        />
                    }
                />
            </>
        }
    />,
    <div key="23">
        <h3>Placement and Arrangement</h3>
        <p>
            As a general rule, there should be no more than three icon buttons in a button group.
            Icon buttons are used when real estate is a concern, offering a more compact option.
            They should be placed to the right of their related content and right-aligned.
        </p>
    </div>,
    <Columns
        key="24"
        columns={Columns.ColumnNumber.TWO}
        content={
            <>
                <div>
                    <h4>Grid action columns</h4>
                    <p>
                        If individual rows in a data grid require actions, Use an action column with
                        icons or a meatball button. In a grid, the actions column or meatball button
                        should be last. Maintaining this placement sets user expectations for where
                        additional actions can be found on grids.
                    </p>
                </div>
                <ContentBox
                    centerContent
                    content={
                        <div style={{ display: 'flex', gap: '16px' }}>
                            <img
                                style={{ width: 93 }}
                                src="https://sseasacdn.blob.core.windows.net/strata-website/Icon-Buttons-Actions-Column.svg"
                                alt="Icon buttons grouped in a grid column"
                                loading="lazy"
                            />
                            <img
                                style={{ width: 48 }}
                                src="https://sseasacdn.blob.core.windows.net/strata-website/Meatball-Column.svg"
                                alt="Meatball icon column in a grid"
                                loading="lazy"
                            />
                        </div>
                    }
                />
            </>
        }
    />,
    <div key="25">
        <h4>Cards and lists</h4>
        <p>
            If a button group is repeated throughout the same section the button groups need to be
            clearly separated into sub sections to ensure clarity.
        </p>
    </div>,
    <div key="26">
        <ContentBox
            centerContent
            content={
                <img
                    style={{ width: 532 }}
                    src="https://sseasacdn.blob.core.windows.net/strata-website/Icon-Buttons-Cards-and-Lists.svg"
                    alt="Icon buttons grouped in a list of cards"
                    loading="lazy"
                />
            }
        />
        <div className="sds_text__caption">
            In this example, each subtask has its own group of icon buttons. Within each subtask,
            there is a section for conditions, which also has its own set of icon buttons. The
            layout is clearly organized, showing that each group of icon buttons belongs to its
            respective section.
        </div>
    </div>,
];

const toggleButton = [
    <p key="21">
        Toggle buttons give users a quick way to switch between two mutually exclusive states, in
        cases where the state change is immediate and easily reversible.
    </p>,
    <Columns
        key="22"
        columns={Columns.ColumnNumber.TWO}
        content={
            <>
                <div>
                    <h3>When to use a toggle button</h3>
                    <p>
                        Use a toggle button when the user needs to select between two opposite or
                        complementary options. Such as dark and light modes or activate and
                        deactivate.
                    </p>
                </div>
                <ContentBox
                    centerContent
                    content={
                        <img
                            style={{ width: 173 }}
                            src="https://sseasacdn.blob.core.windows.net/strata-website/Toggle-Button-Profile-Menu.svg"
                            alt="Toggle button in profile dropdown menu"
                            loading="lazy"
                        />
                    }
                />
                <div>
                    <h3>Placement</h3>
                    <p>
                        As a general rule toggle buttons should be placed to the right of their
                        related content.
                    </p>
                </div>
                <ContentBox
                    centerContent
                    content={
                        <img
                            style={{ width: 197 }}
                            src="https://sseasacdn.blob.core.windows.net/strata-website/Toggle-Button-Placement.svg"
                            alt="Toggle button placed to right of content"
                            loading="lazy"
                        />
                    }
                />
            </>
        }
    />,
];

const hyperlink = [
    <p key="23">
        Hyperlinks allows users to navigate to a different URL or location within the same
        application when clicked.
    </p>,
    <div key="24">
        <h4>When to use a hyperlink</h4>
        <ul>
            <li>Use hyperlinks to share extra details and resources to the user.</li>
            <li>
                Use hyperlinks to allow users to quickly jump to different sections within the same
                page.
            </li>
        </ul>
    </div>,
    <Columns
        key="25"
        columns={Columns.ColumnNumber.TWO}
        content={
            <>
                <div>
                    <h3>Placement</h3>
                    <p>
                        Hyperlinks should be placed at the end of a sentence or section to maintain
                        readability. Placing hyperlinks in the middle of a sentence disrupts the
                        flow of the content.
                    </p>
                </div>
                <ContentBox
                    centerContent
                    content={
                        <img
                            style={{ width: 197 }}
                            src="https://sseasacdn.blob.core.windows.net/strata-website/Hyperlink-Placement.svg"
                            alt="Hyperlink placed in content"
                            loading="lazy"
                        />
                    }
                />
            </>
        }
    />,
];

export const Buttons = () => (
    <div>
        <Page
            pageTitle="Button Usage Guidelines"
            pageDescription={
                <p>Buttons are elements that are used to trigger actions and make choices.</p>
            }
            pageContent={
                <>
                    <Section title="Button Overview" contentArray={buttonOverview} />
                    <Section title="Primary Button" contentArray={primaryButton} />
                    <Section title="Secondary Button" contentArray={secondaryButton} />
                    <Section title="Action Button" contentArray={actionButton} />
                    <Section title="Navigation Button" contentArray={navigationButton} />
                    <Section title="Icon Only Button" contentArray={iconOnlyButton} />
                    <Section title="Toggle Button" contentArray={toggleButton} />
                    <Section title="Hyperlink" contentArray={hyperlink} />
                </>
            }
        />
    </div>
);
